import React, { useRef, useState } from "react";
import { Plus, Trash, UploadSimple, MapPin, Package, Star } from "@phosphor-icons/react";
import {
  createLocation, deleteLocation, uploadLocationImage, setLocationHeroImage,
  createProp, deleteProp, uploadPropImage, setPropHeroImage, fileUrl,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

function EntityCard({ entity, tier, onDelete, onUpload, onSetHero }) {
  const inputRef = useRef();
  const atCap = entity.image_paths.length >= 5;
  const handleFile = (e) => {
    const file = e.target.files[0];
    if (file) onUpload(entity.id, file);
    e.target.value = "";
  };
  return (
    <div data-testid={`${tier}-card-${entity.id}`} className="border border-slate-800 rounded-sm p-3 bg-slate-950/50 space-y-2">
      <div className="flex items-center justify-between">
        <span className="font-mono text-xs uppercase tracking-widest text-amber-400">{entity.name}</span>
        <div className="flex items-center gap-2">
          <span data-testid={`${tier}-image-count-${entity.id}`} className="font-mono text-[9px] text-slate-600">
            {entity.image_paths.length}/5
          </span>
          <button data-testid={`delete-${tier}-btn-${entity.id}`} onClick={() => onDelete(entity.id)} className="text-slate-600 hover:text-rose-500 transition-colors">
            <Trash size={14} />
          </button>
        </div>
      </div>
      {entity.descriptor && <p className="text-xs text-slate-400 leading-relaxed line-clamp-3">{entity.descriptor}</p>}
      <div className="grid grid-cols-3 gap-1.5">
        {entity.image_paths.map((p) => {
          const isHero = entity.hero_image_path === p;
          return (
            <div key={p} className="relative group">
              <img src={fileUrl(p)} alt={entity.name} className={`w-full h-16 object-cover rounded-sm border ${isHero ? "border-amber-400" : "border-slate-800"}`} />
              {isHero ? (
                <span data-testid={`hero-badge-${tier}-${entity.id}`} className="absolute top-0.5 left-0.5 flex items-center gap-0.5 bg-amber-500/90 text-slate-950 text-[9px] font-mono uppercase px-1 rounded-sm">
                  <Star size={8} weight="fill" /> Hero
                </span>
              ) : (
                <button data-testid={`set-hero-btn-${tier}-${entity.id}-${p}`} onClick={() => onSetHero(entity.id, p)} className="absolute inset-0 flex items-center justify-center bg-slate-950/70 opacity-0 group-hover:opacity-100 transition-opacity text-amber-400 text-[9px] font-mono uppercase">
                  <Star size={12} className="mr-1" /> Set Hero
                </button>
              )}
            </div>
          );
        })}
        {!atCap && (
          <button data-testid={`upload-${tier}-image-btn-${entity.id}`} onClick={() => inputRef.current.click()} className="w-full h-16 flex items-center justify-center border border-dashed border-slate-700 rounded-sm text-slate-500 hover:text-amber-400 hover:border-amber-500/50 transition-colors">
            <UploadSimple size={16} />
          </button>
        )}
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
      </div>
    </div>
  );
}

export default function LocationsPropsPanel({ storyId, locations, props, setLocations, setProps }) {
  const [newLocName, setNewLocName] = useState("");
  const [newPropName, setNewPropName] = useState("");

  const addLocation = async () => {
    if (!newLocName.trim()) return;
    const loc = await createLocation(storyId, newLocName.trim());
    setLocations((prev) => [...prev, loc]);
    setNewLocName("");
  };
  const addProp = async () => {
    if (!newPropName.trim()) return;
    const prop = await createProp(storyId, newPropName.trim());
    setProps((prev) => [...prev, prop]);
    setNewPropName("");
  };
  const handleDeleteLocation = async (id) => { await deleteLocation(id); setLocations((prev) => prev.filter((l) => l.id !== id)); };
  const handleDeleteProp = async (id) => { await deleteProp(id); setProps((prev) => prev.filter((p) => p.id !== id)); };
  const handleUploadLocation = async (id, file) => {
    try { const updated = await uploadLocationImage(id, file); setLocations((prev) => prev.map((l) => (l.id === id ? updated : l))); }
    catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
  };
  const handleUploadProp = async (id, file) => {
    try { const updated = await uploadPropImage(id, file); setProps((prev) => prev.map((p) => (p.id === id ? updated : p))); }
    catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
  };
  const handleHeroLocation = async (id, path) => { const updated = await setLocationHeroImage(id, path); setLocations((prev) => prev.map((l) => (l.id === id ? updated : l))); };
  const handleHeroProp = async (id, path) => { const updated = await setPropHeroImage(id, path); setProps((prev) => prev.map((p) => (p.id === id ? updated : p))); };

  return (
    <div data-testid="locations-props-panel" className="space-y-3 border border-amber-500/20 rounded-sm p-3 bg-amber-950/5">
      <h3 className="font-heading uppercase tracking-wider text-xs font-semibold text-amber-400">
        Locations & Props (optional) — Base Reference Images
      </h3>
      <p className="text-[11px] text-slate-500">
        Same idea as Main Characters above — lock a real-world set piece (e.g. "Train Station", "The Train",
        "The Charger") to a base image and every generated shot referencing it will match. Up to 5 images per
        entity; pick a "Hero" to mark the primary reference. Fully optional — skip anything you don't need locked.
      </p>
      <p className="text-[11px] text-slate-600 italic">
        One lock per entity, no variants. A location's PERMANENT state change (e.g. a store that later collapses) should be a
        separately-named entity ("Store (Intact)", "Store (Collapsing)"), not a variant of this one — naming does the work.
      </p>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <div className="flex items-center gap-2"><MapPin size={14} className="text-amber-400" /><span className="text-xs font-mono uppercase text-slate-400">Locations</span></div>
          {locations.map((l) => (
            <EntityCard key={l.id} entity={l} tier="location" onDelete={handleDeleteLocation} onUpload={handleUploadLocation} onSetHero={handleHeroLocation} />
          ))}
          <div className="flex gap-2">
            <Input data-testid="new-location-name-input" value={newLocName} onChange={(e) => setNewLocName(e.target.value)} placeholder="Location name" className="bg-slate-950 border-slate-800 rounded-sm h-8 text-xs" />
            <Button data-testid="add-location-btn" onClick={addLocation} size="sm" className="bg-amber-600 hover:bg-amber-500 rounded-sm shrink-0"><Plus size={14} /></Button>
          </div>
        </div>
        <div className="space-y-2">
          <div className="flex items-center gap-2"><Package size={14} className="text-amber-400" /><span className="text-xs font-mono uppercase text-slate-400">Props</span></div>
          {props.map((p) => (
            <EntityCard key={p.id} entity={p} tier="prop" onDelete={handleDeleteProp} onUpload={handleUploadProp} onSetHero={handleHeroProp} />
          ))}
          <div className="flex gap-2">
            <Input data-testid="new-prop-name-input" value={newPropName} onChange={(e) => setNewPropName(e.target.value)} placeholder="Prop name" className="bg-slate-950 border-slate-800 rounded-sm h-8 text-xs" />
            <Button data-testid="add-prop-btn" onClick={addProp} size="sm" className="bg-amber-600 hover:bg-amber-500 rounded-sm shrink-0"><Plus size={14} /></Button>
          </div>
        </div>
      </div>
    </div>
  );
}
